import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getTeableSheetInfo, TeableSheetInfoLinked } from '../features/surveys/surveysApi';
import { getSocket } from '../lib/socket';
import { showToast } from '../components/ui/toast.utils';

export function SurveyTeableSheetPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheetInfo, setSheetInfo] = useState<TeableSheetInfoLinked | null>(null);
  const [unlinked, setUnlinked] = useState<{ message: string; code: string } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const fetchInfo = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setUnlinked(null);
        const info = await getTeableSheetInfo(id);
        if (info.linked) {
          setSheetInfo(info);
        } else {
          setSheetInfo(null);
          setUnlinked({ message: info.message, code: info.code });
        }
      } catch (err: any) {
        setError(err.message || 'Error al cargar la información de la hoja');
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [id]);

  useEffect(() => {
    if (!id || !sheetInfo) return;

    const socket = getSocket();
    
    // Unirse a la sala de la encuesta
    socket.emit('join:survey', id);

    // Escuchar nuevas respuestas
    const handleNewResponse = (data: { surveyId: string; responseId: string }) => {
      if (data.surveyId === id) {
        showToast('Nueva respuesta recibida. Actualizando hoja...', 'default');
        
        // Refrescar el iframe para mostrar el nuevo registro si Teable no lo hace solo
        if (iframeRef.current) {
          iframeRef.current.contentWindow?.location.reload();
        }
      }
    };

    socket.on('survey:response:created', handleNewResponse);

    return () => {
      socket.off('survey:response:created', handleNewResponse);
    };
  }, [id, sheetInfo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (unlinked) {
    return (
      <div className="p-8 text-center max-w-lg mx-auto space-y-4">
        <h2 className="text-xl font-bold text-foreground">Hoja Teable no disponible</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">{unlinked.message}</p>
        {unlinked.code === 'no_user_config' && (
          <p>
            <Link to="/perfil" className="text-primary font-medium hover:underline">
              Ir a Perfil → integración Teable
            </Link>
          </p>
        )}
        <div className="flex flex-wrap gap-3 justify-center pt-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 border border-border rounded-md text-sm hover:bg-muted"
          >
            Reintentar
          </button>
          <button
            type="button"
            onClick={() => navigate(window.location.pathname.startsWith('/evaluations') ? '/evaluations' : '/surveys')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
          >
            Volver a Encuestas
          </button>
        </div>
      </div>
    );
  }

  if (error || !sheetInfo) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-destructive mb-4">Error</h2>
        <p className="text-muted-foreground mb-6">{error || 'No se encontró información de la hoja'}</p>
        <button
          onClick={() => navigate(window.location.pathname.startsWith('/evaluations') ? '/evaluations' : '/surveys')}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
        >
          Volver a Encuestas
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <div className="bg-card border-b border-border p-4 flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold">{sheetInfo.title}</h1>
          <p className="text-sm text-muted-foreground">
            {sheetInfo.canEdit 
              ? 'Tienes permisos de edición de formato' 
              : 'Vista de solo lectura (puedes ver respuestas en tiempo real)'}
          </p>
        </div>
        <button
          onClick={() => navigate(window.location.pathname.startsWith('/evaluations') ? `/evaluations/${id}/responses` : `/surveys/${id}/responses`)}
          className="text-sm text-primary hover:underline"
        >
          Ver Tabla de Respuestas Local
        </button>
      </div>
      
      <div className="flex-1 bg-muted relative">
        <iframe
          ref={iframeRef}
          src={sheetInfo.embedUrl}
          className="w-full h-full border-none"
          title={`Hoja de Teable - ${sheetInfo.title}`}
          allow="clipboard-read; clipboard-write"
        />
      </div>
    </div>
  );
}
